import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import styles from './Sidebar.module.css';

function ChevronIcon({ direction }: { direction: 'down' | 'right' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: 'transform 150ms', flexShrink: 0 }}
    >
      {direction === 'down' ? (
        <polyline points="6 9 12 15 18 9" />
      ) : (
        <polyline points="9 6 15 12 9 18" />
      )}
    </svg>
  );
}

function MenuIcon({ name, active }: { name: string; active?: boolean }) {
  const iconProps = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style: { flexShrink: 0, opacity: active ? 1 : 0.85 },
  };

  switch (name) {
    case 'dashboard':
      return (
        <svg {...iconProps}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'zap':
      return (
        <svg {...iconProps}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'creditCard':
      return (
        <svg {...iconProps}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      );
    case 'pieChart':
      return (
        <svg {...iconProps}>
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
          <path d="M22 12A10 10 0 0 0 12 2v10z" />
        </svg>
      );
    case 'fileText':
      return (
        <svg {...iconProps}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      );
    case 'file':
      return (
        <svg {...iconProps}>
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case 'plusSquare':
      return (
        <svg {...iconProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...iconProps}>
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      );
    case 'barChart':
      return (
        <svg {...iconProps}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      );
    case 'tag':
      return (
        <svg {...iconProps}>
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      );
    case 'image':
      return (
        <svg {...iconProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      );
    case 'store':
      return (
        <svg {...iconProps}>
          <path d="M3 7h18l-2 9H5z" />
          <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
          <path d="M5 21h14" />
        </svg>
      );
    case 'user':
      return (
        <svg {...iconProps}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'message':
      return (
        <svg {...iconProps}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'plug':
      return (
        <svg {...iconProps}>
          <path d="M12 22v-5" />
          <path d="M9 7V2" />
          <path d="M15 7V2" />
          <path d="M6 13a5 5 0 0 0 10 0V7H6z" />
        </svg>
      );
    case 'gitBranch':
      return (
        <svg {...iconProps}>
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="6" cy="3" r="3" />
          <circle cx="6" cy="15" r="3" />
          <circle cx="18" cy="6" r="3" />
          <path d="M6 8c0 2 2 3 5 3h4" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...iconProps}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    case 'package':
      return (
        <svg {...iconProps}>
          <path d="m21 16-9 5-9-5" />
          <path d="m3 8 9 5 9-5" />
          <path d="M21 8v8" />
          <path d="M3 8v8" />
          <path d="M12 3v10" />
        </svg>
      );
    case 'battery':
      return (
        <svg {...iconProps}>
          <rect x="2" y="7" width="16" height="10" rx="2" />
          <line x1="22" y1="11" x2="22" y2="13" />
          <path d="M6 11l-2 3h3l-1 3" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'cpu':
      return (
        <svg {...iconProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" />
          <line x1="9" y1="1" x2="9" y2="4" />
          <line x1="15" y1="1" x2="15" y2="4" />
          <line x1="9" y1="20" x2="9" y2="23" />
          <line x1="15" y1="20" x2="15" y2="23" />
          <line x1="20" y1="9" x2="23" y2="9" />
          <line x1="20" y1="14" x2="23" y2="14" />
          <line x1="1" y1="9" x2="4" y2="9" />
          <line x1="1" y1="14" x2="4" y2="14" />
        </svg>
      );
    case 'book':
      return (
        <svg {...iconProps}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...iconProps}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...iconProps}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'mapPin':
      return (
        <svg {...iconProps}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case 'archive':
      return (
        <svg {...iconProps}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M8 10h8" />
          <path d="M12 14v-4" />
        </svg>
      );
    default:
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
  }
}

function LogoX({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 204.27 204.27"
      className={className}
      style={style}
      aria-label="TechAssembly Logo"
    >
      <g fill="currentColor">
        <path d="M118.81,62.61a23,23,0,0,1-5,3.77,20.78,20.78,0,0,1-11.72,2.56,20.79,20.79,0,0,1-11.73-2.56,23,23,0,0,1-5-3.77L30.82,8.53A29,29,0,0,1,51.37,0,28.59,28.59,0,0,1,69.21,6.79l32.93,32,32.92-32A28.59,28.59,0,0,1,152.9,0a29,29,0,0,1,20.55,8.53Z" />
        <path d="M118.81,141.66a23,23,0,0,0-5-3.77,21.23,21.23,0,0,0-6.81-2.36,21.58,21.58,0,0,0-4.91-.2,21.65,21.65,0,0,0-4.92.2,21.23,21.23,0,0,0-6.81,2.36,23,23,0,0,0-5,3.77L30.82,195.74a29,29,0,0,0,20.55,8.53,28.54,28.54,0,0,0,17.84-6.79l32.93-32,32.92,32a28.54,28.54,0,0,0,17.84,6.79,29,29,0,0,0,20.55-8.53Z" />
        <path d="M141.66,85.46a23,23,0,0,0-3.77,5,21.23,21.23,0,0,0-2.36,6.81,21.65,21.65,0,0,0-.2,4.92,21.58,21.58,0,0,0,.2 4.91,21.23,21.23,0,0,0,2.36 6.81,23,23,0,0,0,3.77,5l54.08,54.64a29,29,0,0,0,8.53-20.55,28.54,28.54,0,0,0-6.79-17.84l-32-32.92,32-32.93a28.54,28.54,0,0,0,6.79-17.84,29,29,0,0,0-8.53-20.55Z" />
        <path d="M62.61,85.46a23,23,0,0,1,3.77,5,20.79,20.79,0,0,1,2.56,11.73,20.78,20.78,0,0,1-2.56,11.72,23,23,0,0,1-3.77,5L8.53,173.45A29,29,0,0,1,0,152.9a28.59,28.59,0,0,1,6.79-17.84l32-32.92-32-32.93A28.59,28.59,0,0,1,0,51.37,29,29,0,0,1,8.53,30.82Z" />
      </g>
      <g fill="#ff8800" transform="translate(102.14, 102.14) scale(0.72) translate(-210, -216)">
        <path d="M 220.80 236.99 C218.02,235.63 215.50,234.78 213.16,234.41 L 213.15 234.41 C210.44,233.98 208.48,234.25 205.08,235.68 C200.73,237.40 195.46,240.74 188.92,245.91 C187.33,247.15 185.79,248.41 184.39,249.62 L 184.39 249.62 C182.68,251.06 180.89,252.62 179.02,254.28 C177.98,255.08 176.90,255.86 175.99,256.45 C174.05,257.70 171.86,259.46 171.11,260.36 C170.37,261.26 169.32,262.00 168.79,262.00 C168.26,262.00 169.55,259.64 171.64,256.75 C173.74,253.86 177.94,248.12 180.98,243.98 C183.60,240.41 185.87,236.94 187.74,233.68 L 187.74 233.68 C188.54,232.30 189.27,230.96 189.92,229.68 C191.70,226.16 192.86,223.02 193.43,220.02 C193.59,219.21 193.70,218.44 193.76,217.71 C193.83,216.88 193.85,216.06 193.83,215.24 L 193.83 215.24 C193.76,212.52 193.18,209.84 192.11,207.00 C191.79,206.15 191.33,205.12 190.75,203.96 C189.14,200.57 186.86,196.72 183.77,192.08 C174.29,177.85 173.73,176.94 174.16,176.51 C174.62,176.04 181.21,180.53 190.14,187.39 C195.35,191.40 200.77,194.55 205.20,196.29 C208.18,197.51 210.62,198.00 212.42,197.71 C217.37,196.90 222.62,194.31 229.75,188.84 C233.83,185.78 238.57,181.81 244.26,176.75 C247.81,173.59 251.04,171.00 251.43,171.00 C252.91,171.00 250.80,174.94 244.52,183.86 C241.98,187.36 239.76,190.50 237.86,193.36 C233.07,200.49 230.59,205.21 229.47,209.50 C228.42,213.06 228.26,216.25 228.86,219.50 C229.20,222.23 230.11,224.53 232.22,228.72 L 232.22 228.72 C232.28,228.84 232.34,228.96 232.40,229.08 C232.44,229.16 232.49,229.25 232.53,229.33 C234.61,233.39 237.79,238.75 239.68,241.38 C244.55,248.16 248.26,255.00 247.07,255.00 C246.52,255.00 244.59,253.73 242.78,252.19 C234.56,245.14 226.99,239.94 220.80,236.99 Z" />
      </g>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

type NavItem = {
  label: string;
  path: string;
  icon: string;
};

type NavSection = {
  title: string;
  icon: string;
  items: NavItem[];
  subItems?: NavItem[];
};

export const navSections: NavSection[] = [
  {
    title: 'Operate',
    icon: 'dashboard',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
      { label: 'Command Centre', path: '/command-centre', icon: 'zap' },
      { label: 'Checkout', path: '/checkout', icon: 'creditCard' },
    ],
  },
  {
    title: 'Finance',
    icon: 'pieChart',
    items: [
      { label: 'Overview', path: '/finance', icon: 'pieChart' },
      { label: 'Transactions', path: '/transactions', icon: 'fileText' },
      { label: 'Record Transaction', path: '/record-transaction', icon: 'plusSquare' },
      { label: 'Accounts', path: '/accounts', icon: 'layers' },
      { label: 'Invoices', path: '/invoices', icon: 'file' },
    ],
  },
  {
    title: 'Build',
    icon: 'layers',
    items: [
      { label: 'Catalog', path: '/merchant/products', icon: 'tag' },
      { label: 'Forms', path: '/inputs', icon: 'fileText' },
      { label: 'Landing Pages', path: '/pages', icon: 'file' },
      { label: 'Media Library', path: '/media-library', icon: 'image' },
      { label: 'Storefront', path: '/storefront', icon: 'store' },
    ],
  },
  {
    title: 'Engage',
    icon: 'user',
    items: [
      { label: 'Contacts', path: '/contacts', icon: 'user' },
      { label: 'Conversations', path: '/conversations', icon: 'message' },
      { label: 'Integrations', path: '/integrations', icon: 'plug' },
    ],
  },
  {
    title: 'Automate',
    icon: 'gitBranch',
    items: [
      { label: 'Workflows', path: '/workflows', icon: 'gitBranch' },
    ],
    subItems: [
      { label: 'Templates', path: '/templates', icon: 'copy' },
    ],
  },
  {
    title: 'Manage',
    icon: 'settings',
    items: [
      { label: 'Assets', path: '/assets', icon: 'package' },
      { label: 'Staff', path: '/actors', icon: 'users' },
      { label: 'Branches', path: '/branches', icon: 'mapPin' },
      { label: 'Stock inventory', path: '/inventory', icon: 'archive' },
      { label: 'Capacity', path: '/capacity', icon: 'battery' },
      { label: 'Settings', path: '/settings', icon: 'settings' },
    ],
  },
];

export const adminSection: NavSection = {
  title: 'Admin',
  icon: 'shield',
  items: [
    { label: 'Dashboard', path: '/admin', icon: 'dashboard' },
    { label: 'Analytics', path: '/admin/analytics', icon: 'barChart' },
    { label: 'Memory tracker', path: '/admin/memory-tracker', icon: 'cpu' },
    { label: 'Docs', path: '/admin/doc', icon: 'book' },
  ],
};

export function isPathActive(currentPath: string, itemPath: string) {
  if (currentPath === itemPath) return true;
  if (itemPath !== '/' && currentPath.startsWith(itemPath + '/')) return true;
  return false;
}

export function findActiveNavLabel(pathname: string): string | null {
  const allItems = navSections.flatMap((s) => [...s.items, ...(s.subItems ?? [])]);
  allItems.push(...adminSection.items);

  const exact = allItems.find((i) => i.path === pathname);
  if (exact) return exact.label;

  const prefix = allItems.find((i) => isPathActive(pathname, i.path));
  return prefix?.label ?? null;
}

function getInitial(name?: string | null) {
  if (!name) return '?';
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
}

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function handle(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, onClose]);
}

interface SidebarProps {
  mini?: boolean;
  onNavigate?: () => void;
  onToggleMini?: () => void;
  capacityBalance?: number | null;
}

export default function Sidebar({ mini = false, onNavigate, onToggleMini, capacityBalance }: SidebarProps) {
  const { user, currentWorkspace, switchWorkspace, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.id === '1';

  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);
  const flyoutTimer = useRef<number | null>(null);

  const activeSectionTitle = useMemo(() => {
    const allSections = isAdmin ? [...navSections, adminSection] : navSections;
    const hit = allSections.find((s) =>
      [...s.items, ...(s.subItems ?? [])].some((item) => isPathActive(location.pathname, item.path))
    );
    return hit?.title ?? null;
  }, [location.pathname, isAdmin]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedSections((prev) => {
      if (!activeSectionTitle || prev[activeSectionTitle] === true) return prev;
      return { ...prev, [activeSectionTitle]: true };
    });
  }, [activeSectionTitle]);

  const workspaceRef = useRef<HTMLDivElement>(null);

  useClickOutside(workspaceRef, () => setWorkspaceOpen(false));

  const handleLinkClick = useCallback(
    (path: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      navigate(path);
      onNavigate?.();
    },
    [navigate, onNavigate]
  );

  const handleSwitchWorkspace = useCallback(
    (id: string) => () => {
      setWorkspaceOpen(false);
      if (id !== currentWorkspace?.id) switchWorkspace(id);
    },
    [currentWorkspace?.id, switchWorkspace]
  );

  const closeFlyout = useCallback(() => setActiveFlyout(null), []);

  const openFlyout = useCallback((title: string) => {
    if (flyoutTimer.current) window.clearTimeout(flyoutTimer.current);
    setActiveFlyout(title);
  }, []);

  const delayedCloseFlyout = useCallback(() => {
    if (flyoutTimer.current) window.clearTimeout(flyoutTimer.current);
    flyoutTimer.current = window.setTimeout(() => setActiveFlyout(null), 120);
  }, []);

  const workspaces = user?.workspaces ?? [];
  const displayName = user?.full_name || user?.email || 'User';
  const handle = currentWorkspace?.slug ? `@${currentWorkspace.slug}` : currentWorkspace?.name ?? '';

  const renderLink = (item: NavItem, keyPrefix: string, isSub = false) => {
    const active = isPathActive(location.pathname, item.path);
    return (
      <button
        type="button"
        key={`${keyPrefix}-${item.path}`}
        onClick={handleLinkClick(item.path)}
        className={`${styles.link} ${active ? styles.linkActive : ''}`}
      >
        <span className={styles.linkIcon}>
          <MenuIcon name={item.icon} active={active} />
        </span>
        <span className={styles.linkLabel}>{item.label}</span>
      </button>
    );
  };

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const renderSection = (section: NavSection, keyPrefix: string) => {
    const isActiveParent = section.items
      .concat(section.subItems ?? [])
      .some((item) => isPathActive(location.pathname, item.path));
    const isExpanded = expandedSections[section.title] ?? isActiveParent;

    return (
      <div key={keyPrefix} className={styles.section}>
        {!mini && (
          <button
            type="button"
            onClick={() => toggleSection(section.title)}
            className={`${styles.sectionHeader} ${isActiveParent ? styles.sectionHeaderActive : ''}`}
            aria-expanded={isExpanded}
          >
            <span className={styles.sectionTitle}>{section.title}</span>
            <ChevronIcon direction={isExpanded ? 'down' : 'right'} />
          </button>
        )}
        {isExpanded && (
          <div className={styles.sectionItems}>
            {section.items.map((item) => renderLink(item, keyPrefix))}
            {section.subItems && (
              <div className={styles.subMenu}>
                {section.subItems.map((item) => renderLink(item, `${keyPrefix}-sub`, true))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMiniSection = (section: NavSection, keyPrefix: string) => {
    const isActiveParent = section.items
      .concat(section.subItems ?? [])
      .some((item) => isPathActive(location.pathname, item.path));
    const open = activeFlyout === section.title;

    const flyoutLinkClick = (path: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      closeFlyout();
      navigate(path);
      onNavigate?.();
    };

    const flyoutItem = (item: NavItem, isSub = false) => {
      const active = isPathActive(location.pathname, item.path);
      return (
        <button
          type="button"
          key={`flyout-${keyPrefix}-${item.path}`}
          onClick={flyoutLinkClick(item.path)}
          className={`${styles.link} ${active ? styles.flyoutLinkActive : ''}`}
        >
          <span className={styles.linkIcon}>
            <MenuIcon name={item.icon} active={active} />
          </span>
          <span className={styles.linkLabel}>{item.label}</span>
        </button>
      );
    };

    return (
      <div
        key={`mini-${keyPrefix}`}
        className={styles.miniSectionWrap}
        onMouseEnter={() => openFlyout(section.title)}
        onMouseLeave={delayedCloseFlyout}
      >
        <button
          type="button"
          className={`${styles.miniBtn} ${isActiveParent ? styles.miniBtnActive : ''}`}
          title={section.title}
          aria-label={section.title}
          aria-expanded={open}
          onClick={() => setActiveFlyout((p) => (p === section.title ? null : section.title))}
        >
          <MenuIcon name={section.icon} active={isActiveParent} />
        </button>
        {open && (
          <div className={styles.flyout} onMouseEnter={() => openFlyout(section.title)} onMouseLeave={delayedCloseFlyout}>
            <div className={styles.flyoutHeader}>{section.title}</div>
            {section.items.map((item) => flyoutItem(item))}
            {section.subItems?.map((item) => flyoutItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={styles.sidebar}>
      <div className={`${styles.header} ${mini ? styles.headerMini : ''}`}>
        <button
          type="button"
          className={`${styles.collapseBtn} ${mini ? styles.collapseBtnMini : ''}`}
          onClick={onToggleMini}
          aria-label={mini ? 'Expand sidebar' : 'Collapse sidebar'}
          title={mini ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {mini ? '›' : '‹'}
        </button>
        <button
          type="button"
          onClick={handleLinkClick('/dashboard')}
          className={styles.logoBtn}
          title="Dashboard"
        >
          <LogoX className={styles.logoIcon} />
          {!mini && (
            <span className={styles.brandText}>
              <span className={styles.brandName}>TechAssembly</span>
              <span className={styles.brandTagline}>Be Ahead</span>
            </span>
          )}
        </button>
      </div>

      <div className={`${styles.profileCard} ${mini ? styles.profileCardMini : ''}`}>
        <div className={`${styles.profileTop} ${mini ? styles.profileTopMini : ''}`}>
          <div className={styles.avatar}>
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" />
            ) : (
              getInitial(user?.full_name || user?.email)
            )}
          </div>
          {!mini && (
            <div className={styles.profileInfo}>
              <span className={styles.profileName}>{displayName}</span>
              <span className={styles.profileHandle}>{handle}</span>
            </div>
          )}
        </div>
        {!mini && (
          <div className={styles.profileActions}>
            <div className={styles.dropdownAnchor} ref={workspaceRef}>
              <button
                type="button"
                className={styles.manageLink}
                onClick={() => setWorkspaceOpen((v) => !v)}
                aria-expanded={workspaceOpen}
              >
                Manage workspace
              </button>
              {workspaceOpen && (
                <div className={styles.dropdown}>
                  {workspaces.length === 0 && (
                    <p className={styles.dropdownHint}>No workspaces</p>
                  )}
                  {workspaces.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      className={`${styles.dropdownItem} ${w.id === currentWorkspace?.id ? styles.dropdownItemActive : ''}`}
                      onClick={handleSwitchWorkspace(w.id)}
                    >
                      {w.name}
                      <span className={styles.dropdownRole}>{w.role}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.dropdownNewWorkspace}
                    onClick={() => {
                      setWorkspaceOpen(false);
                      navigate('/onboarding');
                      onNavigate?.();
                    }}
                  >
                    <span>+</span>
                    New workspace
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {mini ? (
        <nav className={styles.navMini}>
          {navSections.map((section) => renderMiniSection(section, section.title))}
          {isAdmin && renderMiniSection(adminSection, 'Admin')}
        </nav>
      ) : (
        <nav className={styles.nav}>
          {navSections.map((section) => renderSection(section, section.title))}
          {isAdmin && renderSection(adminSection, 'Admin')}
        </nav>
      )}

      <div className={`${styles.bottomPanel} ${mini ? styles.bottomPanelMini : ''}`}>
        <div className={`${styles.bottomUser} ${mini ? styles.bottomUserMini : ''}`}>
          <div className={styles.avatar}>
            {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : getInitial(user?.full_name || user?.email)}
          </div>
          {!mini && <span className={styles.bottomName}>{displayName}</span>}
        </div>
        <div className={`${styles.bottomCapacity} ${mini ? styles.bottomCapacityMini : ''}`}>
          <span className={styles.capacityRow}>
            <MenuIcon name="zap" />
            {capacityBalance ?? '—'}
            {!mini && 'Capacity'}
          </span>
          {!mini && (
            <button type="button" className={styles.signOut} onClick={logout}>
              Sign Out
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

export { MoonIcon, HomeIcon };
